import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { callLogAPI } from '../../services/api';
import { useRecordingCache } from '../../hooks/useRecordingCache';

const SPEEDS = [1, 1.5, 2];

const unavailableMessage = (error, info) => {
  const status = error?.response?.status;
  if (status === 403) return "You don't have access to call recordings.";
  if (status === 404) return 'Teams has no recording for this call.';
  if (info && info.status === 'Processing') return 'Teams is still processing this recording. Try again in a few minutes.';
  if (info && info.status !== 'Available') return 'Teams has no recording for this call.';
  return "The recording couldn't be streamed from Teams. Try again in a moment.";
};

/**
 * Stream-only recording player (mockup 13-call-detail.html: play button, speaker waveform, clock, speed).
 * Nothing is downloaded until Play: then a 60-second streaming token is fetched and the audio element streams
 * from the Api proxy. `bars` is the waveform ({ x, y, w, h, color } in a 600×56 box). The parent seeks through the
 * ref (`seek(seconds)`, used by transcript timestamps and evidence quotes) and hears the position through
 * `onPosition`, so the transcript can follow playback.
 */
export const RecordingPlayer = forwardRef(function RecordingPlayer({ callId, durationSeconds, bars = [], onPosition, legend }, ref) {
  const audioRef = useRef(null);
  const waveRef = useRef(null);
  const { getCached, setCache, clearCache } = useRecordingCache();
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [speed, setSpeed] = useState(1);
  const pendingPlay = useRef(false);
  const length = Math.max(0, durationSeconds || 0);

  const moveTo = useCallback(
    (seconds) => {
      const next = Math.max(0, Math.min(length || seconds, seconds));
      setPosition(next);
      if (onPosition) onPosition(next);
      if (audioRef.current && source) audioRef.current.currentTime = next;
    },
    [length, onPosition, source]
  );

  useImperativeHandle(ref, () => ({ seek: moveTo }), [moveTo]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, source]);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      let info = getCached(callId);
      if (!info) {
        info = await callLogAPI.getCallRecording(callId);
        setCache(callId, info);
      }
      if (!info || info.status !== 'Available') {
        setMessage(unavailableMessage(null, info || {}));
        return;
      }
      const token = await callLogAPI.getRecordingStreamToken(callId);
      pendingPlay.current = true;
      setSource(callLogAPI.getRecordingStreamUrl(callId, token.token));
    } catch (error) {
      clearCache(callId);
      setMessage(unavailableMessage(error));
    } finally {
      setLoading(false);
    }
  }, [callId, getCached, setCache, clearCache]);

  const toggle = () => {
    if (!source) {
      load();
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => setMessage("The recording couldn't be played."));
    else audio.pause();
  };

  const onWaveClick = (e) => {
    const rect = waveRef.current.getBoundingClientRect();
    moveTo(((e.clientX - rect.left) / rect.width) * length);
  };

  return (
    <>
      <div className="player" data-testid="recording-state" data-state={loading ? 'loading' : playing ? 'playing' : message ? 'unavailable' : source ? 'paused' : 'idle'}>
        <button className="play" type="button" id="play" aria-label={playing ? 'Pause recording' : 'Play recording'} onClick={toggle} disabled={loading}>
          <Icon name={playing ? 'pause' : 'play'} />
        </button>
        <div
          className="wave"
          id="wave"
          ref={waveRef}
          role="slider"
          tabIndex={0}
          aria-label="Recording position"
          aria-valuemin={0}
          aria-valuemax={Math.round(length)}
          aria-valuenow={Math.round(position)}
          aria-valuetext={`${fmt.mmss(position)} of ${fmt.mmss(length)}`}
          onClick={onWaveClick}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') moveTo(position + 5);
            if (e.key === 'ArrowLeft') moveTo(position - 5);
          }}
        >
          <svg viewBox="0 0 600 56" preserveAspectRatio="none" aria-hidden="true">
            {bars.map((b, i) => (
              <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} rx="1.2" style={{ fill: b.color }} />
            ))}
          </svg>
          <span className="head" style={{ left: `${length ? (position / length) * 100 : 0}%` }} />
        </div>
        <div className="small num" style={{ width: 92, textAlign: 'right' }} id="clock">
          {fmt.mmss(position)} / {fmt.mmss(length)}
        </div>
        {source && (
          <audio
            ref={audioRef}
            src={source}
            preload="auto"
            onLoadedMetadata={(e) => {
              e.currentTarget.currentTime = position;
              e.currentTarget.playbackRate = speed;
              if (pendingPlay.current) {
                pendingPlay.current = false;
                e.currentTarget.play().catch(() => setMessage("The recording couldn't be played."));
              }
            }}
            onTimeUpdate={(e) => {
              setPosition(e.currentTarget.currentTime);
              if (onPosition) onPosition(e.currentTarget.currentTime);
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onError={() => {
              setPlaying(false);
              setSource(null);
              clearCache(callId);
              setMessage("The recording couldn't be streamed from Teams. Try again in a moment.");
            }}
          />
        )}
      </div>
      <div className="row between mt-3">
        {legend}
        <div className="seg" role="group" aria-label="Playback speed">
          {SPEEDS.map((s) => (
            <button key={s} type="button" aria-pressed={speed === s ? 'true' : 'false'} onClick={() => setSpeed(s)}>
              {s}×
            </button>
          ))}
        </div>
      </div>
      {(message || loading) && (
        <div className="caption mt-2" role="status">
          {loading ? 'Connecting to the recording in Teams…' : message}
        </div>
      )}
    </>
  );
});

export default RecordingPlayer;
